import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Typography,
    Drawer,
    useTheme,
    Tooltip,
    CircularProgress,
    Alert,
    Button,
} from '@mui/material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import GeminiWidget from './GeminiWidget'; 

interface Resource {
    name: string;
    description: string;
    url: string;
}

const ExternalResourcesSidebar: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [resources, setResources] = useState<Resource[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [geminiWidgetData, setGeminiWidgetData] = useState<{ apiKey: string } | null>(null); 

    useEffect(() => {
        const loadResources = async () => {
            try {
                const response = await fetch('/externalResources.json');
                if (response.ok) {
                    setResources(await response.json());
                } else {
                    setResources([
                        { "name": "Herramienta IA GROQ", "description": "Chat médico avanzado.", "url": "/ai-assistant" },
                        { "name": "Vademecum (Venezuela)", "description": "Información farmacológica.", "url": "https://www.vademecum.es/venezuela/ve/alfa" },
                        { "name": "MDCalc", "description": "Calculadoras clínicas.", "url": "https://www.mdcalc.com/" },
                        { "name": "Manual MSD", "description": "Diagnóstico y tratamiento.", "url": "https://www.msdmanuals.com/es/professional" },
                        { "name": "Inner body", "description": "Anatomía Humana 3D.", "url": "https://www.innerbody.com/htm/body.html" },
                        { "name": "Farmatodo", "description": "Farmacia.", "url": "https://www.farmatodo.com.ve/" }
                    ]);
                }
            } catch (error) {
                console.error(error);
                setError("Error cargando recursos");
            } finally { setIsLoading(false); }
        };
        loadResources();
    }, []);

    const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) return;
        setIsOpen(open);
    };

    // ✅ FUNCIÓN CORREGIDA: Ventana "Modo Aplicación" Centrada
    const openExternalPopup = (url: string) => {
        // Usamos window.outerWidth para basarnos en el tamaño de la APP, no de la pantalla completa
        const w = window.outerWidth * 0.85; 
        const h = window.outerHeight * 0.85; 
        
        // Centrar exactamente sobre la ventana actual
        const left = window.screenX + (window.outerWidth - w) / 2;
        const top = window.screenY + (window.outerHeight - h) / 2;

        // 'toolbar=no' quita la barra de búsqueda y botones (look limpio)
        // 'location=no' intenta ocultar la URL (aunque navegadores modernos fuerzan una barrita gris pequeña)
        window.open(
            url, 
            'LuiatroExternalResource', 
            `width=${w},height=${h},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
        );
    };

    const handleResourceClick = (item: Resource) => {
        setIsOpen(false); 
        if (item.name.includes('Gemini') || item.name.includes('Herramienta IA') || item.name.includes('GROQ')) {
            if (item.url.startsWith('/')) navigate(item.url);
            else setGeminiWidgetData({ apiKey: 'PLACEHOLDER' });
            return;
        }
        if (item.url.startsWith('/')) { navigate(item.url); return; } 
        
        openExternalPopup(item.url);
    };

    return (
        <>
            <IconButton
                color="primary" onClick={toggleDrawer(!isOpen)}
                sx={{
                    position: 'fixed', top: '50%', right: 0, transform: 'translateY(-50%)',
                    zIndex: theme.zIndex.drawer + 1, bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`, borderRadius: '4px 0 0 4px', boxShadow: 3,
                    '&:hover': { bgcolor: theme.palette.grey[100] }
                }}
            >
                {isOpen ? <CloseIcon /> : <MenuOpenIcon />}
            </IconButton>

            <Drawer anchor="right" open={isOpen} onClose={toggleDrawer(false)}>
                <Box sx={{ width: 350, p: 2 }}>
                    <Typography variant="h6" gutterBottom>Recursos Externos</Typography>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
                    ) : (
                        <List>
                            {resources.map((item) => (
                                <Tooltip key={item.name} title={item.description} placement="left" arrow>
                                    <ListItem button onClick={() => handleResourceClick(item)} sx={{ borderRadius: 1, mb: 1, '&:hover': { bgcolor: 'action.hover' } }}
                                        secondaryAction={!item.url.startsWith('/') && <OpenInNewIcon color="action" fontSize="small" />}
                                    >
                                        <ListItemText primary={item.name} secondary={item.description} secondaryTypographyProps={{ fontSize: '0.8rem' }} />
                                    </ListItem>
                                </Tooltip>
                            ))}
                        </List>
                    )}
                </Box>
            </Drawer>

            {geminiWidgetData && (
                <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.5)', zIndex: 9999 }} onClick={() => setGeminiWidgetData(null)}>
                    <Box sx={{ position: 'absolute', top: '50%', right: '50%', transform: 'translate(50%, -50%)', width: '80vw', height: '80vh', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, maxWidth: 800 }} onClick={(e) => e.stopPropagation()}>
                        <GeminiWidget apiKey={geminiWidgetData.apiKey} onClose={() => setGeminiWidgetData(null)} />
                    </Box>
                </Box>
            )}
        </>
    );
};

export default ExternalResourcesSidebar;